import React from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash.clonedeep';
import uniqBy from 'lodash.uniqby';
import isEmpty from 'lodash.isempty';

import {
	loadAdtFilterOptions,
	loadAdtProviderFilterOptions,
	loadExtendedFilterOptions,
} from './api';
import { Button, Checkbox, ProfileTabs } from '../../../components';

const PAGE_SIZE = 50;

export default class ADTNotificationPrefs extends React.Component {
	static propTypes = {
		user: PropTypes.object.isRequired,
		showNotification: PropTypes.func,
	};

	static defaultProps = {
		user: {},
	};

	state = {
		isDirty: false,
		isSaving: false,
		filterOptions: {},
		filterSelections: {
			provider_filter: null,
			adt_types: null,
			adt_class: null,
			employers: [],
			locations: [],
			programs: [],
		},
		providerOffset: 0,
		firstNameSearch: '',
		lastNameSearch: '',
	};

	componentDidMount() {
		const user = this.props.user.toJSON();
		this.setState({ isLoadingFilterOptions: true });

		Promise.allSettled([
			loadAdtProviderFilterOptions(user.id),
			loadExtendedFilterOptions(user.id),
			loadAdtFilterOptions(user.id),
		]).then(
			([
				{
					value: { filter_options: providerOptions },
				},
				{
					value: { filter_options: extendedOptions },
				},
				{
					value: { filter_options },
				},
			]) => {
				this.setState({
					isLoadingFilterOptions: false,
					filterSelections: {
						...this.state.filterSelections,
						...user.adt_notification_filter,
					},
					filterOptions: {
						...providerOptions,
						...filter_options,
						...extendedOptions.extended_filter,
					},
				});
			}
		);
	}

	handleLoadMoreProviders = (event) => {
		event.preventDefault();
		const user = this.props.user.toJSON();
		const { providerOffset, filterOptions, firstNameSearch, lastNameSearch } =
			this.state;
		let name_first, name_last;
		this.setState({
			isLoadingFilterOptions: true,
			providerOffset: providerOffset + PAGE_SIZE,
		});
		if (!isEmpty(firstNameSearch)) {
			name_first = firstNameSearch;
		}
		if (!isEmpty(lastNameSearch)) {
			name_last = lastNameSearch;
		}
		loadAdtProviderFilterOptions(user.id, {
			record_limit: PAGE_SIZE,
			offset: providerOffset + PAGE_SIZE,
			name_first,
			name_last,
		}).then(({ filter_options }) => {
			this.setState({
				isLoadingFilterOptions: false,
				filterOptions: {
					...filterOptions,
					provider_filter: uniqBy(
						[
							...filterOptions.provider_filter,
							...filter_options.provider_filter,
						],
						'Provider_NPI'
					),
				},
			});
		});
	};

	handleProviderNameSearch = () => {
		const user = this.props.user.toJSON();
		const { filterOptions, firstNameSearch, lastNameSearch } = this.state;
		let name_first, name_last;
		this.setState({ isLoadingFilterOptions: true, providerOffset: 0 });
		if (!isEmpty(firstNameSearch)) {
			name_first = firstNameSearch;
		}
		if (!isEmpty(lastNameSearch)) {
			name_last = lastNameSearch;
		}
		loadAdtProviderFilterOptions(user.id, {
			record_limit: PAGE_SIZE,
			offset: 0,
			name_first,
			name_last,
		}).then(({ filter_options }) => {
			this.setState({
				isLoadingFilterOptions: false,
				filterOptions: {
					...filterOptions,
					provider_filter: filter_options.provider_filter,
				},
			});
		});
	};

	handleClearAll = ({ target }) => {
		this.setState((currState) => ({
			isDirty: true,
			filterSelections: {
				...currState.filterSelections,
				[target.name]: [],
			},
		}));
	};

	handleSelectAll = ({ target }) => {
		this.setState((currState) => ({
			isDirty: true,
			filterSelections: {
				...currState.filterSelections,
				[target.name]: [...this.state.filterOptions[target.name]],
			},
		}));
	};

	handleArrayValueToggle = ({ target }) => {
		const [name, prop] = target.name.split('.');
		if (target.checked) {
			this.setState((currState) => {
				const { filterSelections } = currState;
				if (!filterSelections[name]) {
					filterSelections[name] = [];
				}
				return {
					isDirty: true,
					filterSelections: {
						...filterSelections,
						[name]: [...filterSelections[name], { [prop]: target.value }],
					},
				};
			});
		} else {
			this.setState((currState) => {
				let currentSelections = currState.filterSelections[name];
				if (!Array.isArray(currentSelections)) {
					currentSelections = [...this.state.filterOptions[name]];
				}
				currentSelections = currentSelections.filter(
					(arrayVal) => arrayVal[prop] !== target.value
				);
				return {
					isDirty: true,
					filterSelections: {
						...currState.filterSelections,
						[name]: currentSelections,
					},
				};
			});
		}
	};

	handleSave = () => {
		const { filterSelections, filterOptions } = this.state;
		const { user } = this.props;

		const localSelections = cloneDeep(filterSelections);

		Object.keys(localSelections).forEach((key) => {
			if (
				Array.isArray(localSelections[key]) &&
				localSelections[key].length === filterOptions[key].length
			) {
				if (
					!['employers', 'locations', 'programs', 'provider_filter'].includes(
						key
					)
				) {
					localSelections[key] = null;
				}
			}
		});
		if (Array.isArray(localSelections.provider_filter)) {
			localSelections.provider_filter = localSelections.provider_filter.map(
				({ Provider_NPI }) => ({ Provider_NPI })
			);
		}
		if (Array.isArray(localSelections.adt_types)) {
			localSelections.adt_types = localSelections.adt_types.map(
				({ adt_type }) => ({ adt_type })
			);
		}
		if (Array.isArray(localSelections.adt_class)) {
			localSelections.adt_class = localSelections.adt_class.map(
				({ adt_class_type }) => ({ adt_class_type })
			);
		}

		this.setState({ isSaving: true });
		user.set('adt_notification_filter', localSelections);
		user
			.save()
			.done((updatedUser) => {
				this.setState({
					isSaving: false,
					isDirty: false,
					filterSelections: updatedUser.adt_notification_filter,
					firstNameSearch: '',
					lastNameSearch: '',
				});
			})
			.fail(() => {
				this.setState({ isSaving: false });
				this.props.showNotification({
					message:
						'There was a problem saving your ADT Notification Preferences! Please try again.',
					status: 'danger',
					timeout: 10000,
				});
			});
	};

	render() {
		const {
			filterOptions,
			filterSelections,
			isDirty,
			isSaving,
			isLoadingFilterOptions,
			firstNameSearch,
			lastNameSearch,
		} = this.state;

		let totalProviderSelections = 0,
			totalTypes = 0,
			totalTypeSelections = 0,
			totalClasses = 0,
			totalClassSelections = 0;
		let totalEmployers = 0,
			totalEmployerSelections = 0,
			totalLocations = 0,
			totalLocationSelections,
			totalPrograms = 0,
			totalProgramSelections = 0;

		if (filterSelections && Array.isArray(filterSelections.provider_filter)) {
			totalProviderSelections = filterSelections.provider_filter.length;
		}
		if (filterOptions && Array.isArray(filterOptions.adt_types)) {
			totalTypes = filterOptions.adt_types.length;
			totalTypeSelections = totalTypes;
		}
		if (filterSelections && Array.isArray(filterSelections.adt_types)) {
			totalTypeSelections = filterSelections.adt_types.length;
		}
		if (filterOptions && Array.isArray(filterOptions.adt_class)) {
			totalClasses = filterOptions.adt_class.length;
			totalClassSelections = totalClasses;
		}
		if (filterSelections && Array.isArray(filterSelections.adt_class)) {
			totalClassSelections = filterSelections.adt_class.length;
		}
		if (Array.isArray(filterOptions.employers)) {
			totalEmployers = filterOptions.employers.length;
		}
		if (Array.isArray(filterSelections.employers)) {
			totalEmployerSelections = filterSelections.employers.length;
		}
		if (Array.isArray(filterOptions.locations)) {
			totalLocations = filterOptions.locations.length;
		}
		if (Array.isArray(filterSelections.locations)) {
			totalLocationSelections = filterSelections.locations.length;
		}
		if (Array.isArray(filterOptions.programs)) {
			totalPrograms = filterOptions.programs.length;
		}
		if (Array.isArray(filterSelections.programs)) {
			totalProgramSelections = filterSelections.programs.length;
		}

		return (
			<div className="content-wrapper adt-notification-prefs">
				<ProfileTabs activeTab="adt-notification-prefs" />
				<div className="container mt-xl">
					<div className="row">
						<div className="col-sm-12 text-right">
							{isSaving && (
								<span>
									<em className="fa fa-spin fa-circle-o-notch mr" />
								</span>
							)}
							<Button
								title="Save Changes"
								onClick={this.handleSave}
								primary
								disabled={!isDirty}
							/>
						</div>
					</div>
					<fieldset>
						<legend>
							Providers (
							{totalProviderSelections > 0
								? totalProviderSelections
								: 'No filter applied'}
							)
							<Button
								title="Clear All"
								onClick={this.handleClearAll}
								info
								small
								name="provider_filter"
								className="ml mt mb"
							/>
						</legend>
						<div className="row">
							<div className="col-sm-12 form-inline">
								<div className="form-group">
									<label htmlFor="first-name-search" className="sr-only">
										First Name
									</label>
									<input
										id="first-name-search"
										type="text"
										placeholder="First name"
										className="form-control"
										value={firstNameSearch}
										onChange={({ target }) =>
											this.setState({ firstNameSearch: target.value })
										}
									/>
								</div>
								<div className="form-group ml">
									<label htmlFor="last-name-search" className="sr-only">
										Last Name
									</label>
									<input
										id="last-name-search"
										type="text"
										placeholder="Last name"
										className="form-control"
										value={lastNameSearch}
										onChange={({ target }) =>
											this.setState({ lastNameSearch: target.value })
										}
									/>
								</div>
								<Button
									title="Search"
									onClick={this.handleProviderNameSearch}
									primary
									className="ml"
								/>
							</div>
						</div>
						<div className="row">
							{filterOptions &&
								filterOptions.provider_filter &&
								filterOptions.provider_filter.map((provider) => (
									<div className="col-sm-3" key={provider.Provider_NPI}>
										<Checkbox
											checked={
												Array.isArray(filterSelections.provider_filter) &&
												Boolean(
													filterSelections.provider_filter.find(
														(p) =>
															`${p.Provider_NPI}` === `${provider.Provider_NPI}`
													)
												)
											}
											onChange={this.handleArrayValueToggle}
											label={`${provider.Provider_First_Name} ${provider.Provider_Last_Name}`}
											name="provider_filter.Provider_NPI"
											value={provider.Provider_NPI}
										/>
									</div>
								))}
							{isLoadingFilterOptions && (
								<div className="col-sm-12">
									<em className="fa fa-spin fa-circle-o-notch" />
								</div>
							)}
							{!isLoadingFilterOptions &&
								filterOptions &&
								filterOptions.provider_filter &&
								filterOptions.provider_filter.length % PAGE_SIZE === 0 && (
									<div className="col-sm-3 col-sm-offset-9">
										<a
											href="#"
											className="btn btn-link"
											onClick={this.handleLoadMoreProviders}
										>
											Load more...
										</a>
									</div>
								)}
						</div>
					</fieldset>
					<fieldset>
						<legend>
							ADT Types ({totalTypeSelections} of {totalTypes})
							<Button
								title="Clear All"
								onClick={this.handleClearAll}
								info
								small
								name="adt_types"
								className="ml mt mb"
							/>{' '}
							<Button
								title="Select All"
								onClick={this.handleSelectAll}
								info
								small
								name="adt_types"
								className="ml-xs mt mb"
							/>
						</legend>
						<div className="row">
							{filterOptions &&
								filterOptions.adt_types &&
								filterOptions.adt_types.map((adtType) => (
									<div className="col-sm-4" key={adtType.adt_type}>
										<Checkbox
											checked={
												!filterSelections.adt_types ||
												Boolean(
													filterSelections.adt_types.find(
														(at) => at.adt_type === adtType.adt_type
													)
												)
											}
											onChange={this.handleArrayValueToggle}
											label={adtType.name}
											name="adt_types.adt_type"
											value={adtType.adt_type}
										/>
									</div>
								))}
						</div>
					</fieldset>
					<fieldset>
						<legend>
							ADT Classes ({totalClassSelections} of {totalClasses})
							<Button
								title="Clear All"
								onClick={this.handleClearAll}
								info
								small
								name="adt_class"
								className="ml mt mb"
							/>{' '}
							<Button
								title="Select All"
								onClick={this.handleSelectAll}
								info
								small
								name="adt_class"
								className="ml-xs mt mb"
							/>
						</legend>
						<div className="row">
							{filterOptions &&
								filterOptions.adt_class &&
								filterOptions.adt_class.map((cl) => (
									<div className="col-sm-4" key={cl.adt_class_type}>
										<Checkbox
											checked={
												!filterSelections.adt_class ||
												Boolean(
													filterSelections.adt_class.find(
														(ac) => ac.adt_class_type === cl.adt_class_type
													)
												)
											}
											onChange={this.handleArrayValueToggle}
											label={cl.adt_class_description}
											name="adt_class.adt_class_type"
											value={cl.adt_class_type}
										/>
									</div>
								))}
						</div>
					</fieldset>
					<fieldset>
						<legend>
							Employers ({totalEmployerSelections} of {totalEmployers})
							<Button
								title="Clear All"
								onClick={this.handleClearAll}
								info
								small
								name="employers"
								className="ml mt mb"
							/>{' '}
							<Button
								title="Select All"
								onClick={this.handleSelectAll}
								info
								small
								name="employers"
								className="ml-xs mt mb"
							/>
						</legend>
						<div className="row">
							{filterOptions &&
								filterOptions.employers &&
								filterOptions.employers.map((emp) => (
									<div className="col-sm-4" key={emp.employer}>
										<Checkbox
											checked={
												filterSelections.employers &&
												Boolean(
													filterSelections.employers.find(
														(e) => e.employer === emp.employer
													)
												)
											}
											onChange={this.handleArrayValueToggle}
											label={emp.employer}
											name="employers.employer"
											value={emp.employer}
										/>
									</div>
								))}
						</div>
					</fieldset>
					<fieldset>
						<legend>
							Locations ({totalLocationSelections} of {totalLocations})
							<Button
								title="Clear All"
								onClick={this.handleClearAll}
								info
								small
								name="locations"
								className="ml mt mb"
							/>{' '}
							<Button
								title="Select All"
								onClick={this.handleSelectAll}
								info
								small
								name="locations"
								className="ml-xs mt mb"
							/>
						</legend>
						<div className="row">
							{filterOptions &&
								filterOptions.locations &&
								filterOptions.locations.map((loc) => (
									<div className="col-sm-4" key={loc.location}>
										<Checkbox
											checked={
												filterSelections.locations &&
												Boolean(
													filterSelections.locations.find(
														(l) => l.location === loc.location
													)
												)
											}
											onChange={this.handleArrayValueToggle}
											label={loc.location}
											name="locations.location"
											value={loc.location}
										/>
									</div>
								))}
						</div>
					</fieldset>
					<fieldset>
						<legend>
							Special Programs ({totalProgramSelections} of {totalPrograms})
							<Button
								title="Clear All"
								onClick={this.handleClearAll}
								info
								small
								name="programs"
								className="ml mt mb"
							/>{' '}
							<Button
								title="Select All"
								onClick={this.handleSelectAll}
								info
								small
								name="programs"
								className="ml-xs mt mb"
							/>
						</legend>
						<div className="row">
							{filterOptions &&
								filterOptions.programs &&
								filterOptions.programs.map((prog) => (
									<div className="col-sm-4" key={prog.program}>
										<Checkbox
											checked={
												filterSelections.programs &&
												Boolean(
													filterSelections.programs.find(
														(p) => p.program === prog.program
													)
												)
											}
											onChange={this.handleArrayValueToggle}
											label={prog.program}
											name="programs.program"
											value={prog.program}
										/>
									</div>
								))}
						</div>
					</fieldset>
				</div>
			</div>
		);
	}
}
